/**
 * Data Entry Center — Phase 2 standalone module.
 *
 * Tabs: Forms · My Drafts · My Submissions · Pending Approvals · Bulk Import · Access Matrix (super_admin)
 * Forms get dynamic field rendering. Drafts auto-save every 2s (debounced).
 * Approval workflow: pending → approve/reject. Locked entries cannot be re-edited.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ClipboardList, FileText, Inbox, CheckCircle2, XCircle, Clock, Upload,
  Sparkles, Trash2, Send, Search, Save, ShieldCheck, AlertCircle, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { FORM_FIELDS } from '../lib/dataEntryFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  pending: 'bg-orange-50 text-orange-700 border-orange-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_ICONS = { draft: FileText, pending: Clock, approved: CheckCircle2, rejected: XCircle };

const DataEntryCenterPage = () => {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [tab, setTab] = useState('forms');
  const [activeForm, setActiveForm] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchForms = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/data-entry/forms`);
      setForms(res.data.forms || []);
    } catch (e) { toast.error('Failed to load forms'); }
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/data-entry/submissions/pending-count`);
      setPendingCount(res.data.count || 0);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchForms(); fetchPendingCount(); }, [fetchForms, fetchPendingCount]);

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-heading font-bold">Data Entry Center</h1>
          <p className="text-sm text-muted-foreground">Submit records, save drafts, manage approvals.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full">
          <TabsTrigger value="forms" data-testid="de-tab-forms"><Sparkles className="h-3.5 w-3.5 mr-1" />Forms</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="de-tab-drafts"><Save className="h-3.5 w-3.5 mr-1" />My Drafts</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="de-tab-submissions"><FileText className="h-3.5 w-3.5 mr-1" />My Submissions</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="pending" data-testid="de-tab-pending">
              <Inbox className="h-3.5 w-3.5 mr-1" />Pending
              {pendingCount > 0 && <Badge className="ml-1 bg-orange-500 text-white">{pendingCount}</Badge>}
            </TabsTrigger>
          )}
          <TabsTrigger value="bulk" data-testid="de-tab-bulk"><Upload className="h-3.5 w-3.5 mr-1" />Bulk Import</TabsTrigger>
        </TabsList>

        <TabsContent value="forms" className="mt-4"><FormsCatalog forms={forms} onOpen={setActiveForm} /></TabsContent>
        <TabsContent value="drafts" className="mt-4"><MyDrafts forms={forms} onContinue={(d) => setActiveForm({ ...forms.find((f) => f.key === d.form_key), draft: d })} /></TabsContent>
        <TabsContent value="submissions" className="mt-4"><SubmissionsList mineOnly forms={forms} /></TabsContent>
        {isSuperAdmin && <TabsContent value="pending" className="mt-4"><PendingApprovals onChange={fetchPendingCount} /></TabsContent>}
        <TabsContent value="bulk" className="mt-4"><BulkImport forms={forms} /></TabsContent>
      </Tabs>

      {activeForm && (
        <FormDialog
          form={activeForm}
          initialPayload={activeForm.draft?.payload || {}}
          draftId={activeForm.draft?.id}
          onClose={() => setActiveForm(null)}
          onSubmitted={() => { setActiveForm(null); fetchPendingCount(); }}
        />
      )}
    </div>
  );
};

// ============ Forms Catalog ============
const FormsCatalog = ({ forms, onOpen }) => {
  const [search, setSearch] = useState('');
  const categories = useMemo(() => Array.from(new Set(forms.map((f) => f.category))).sort(), [forms]);
  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return forms.filter((f) => !s || f.label.toLowerCase().includes(s) || f.category.toLowerCase().includes(s));
  }, [forms, search]);

  if (forms.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>You don't have access to any forms yet. Ask your Super Admin to grant access.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search forms..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="de-form-search" />
      </div>
      {categories.map((cat) => {
        const items = filtered.filter((f) => f.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat}>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{cat}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((f) => (
                <Card
                  key={f.key}
                  className="cursor-pointer hover:shadow-md hover:border-primary transition-all"
                  onClick={() => onOpen(f)}
                  data-testid={`de-form-card-${f.key}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{f.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        → {f.target}
                        {f.requires_approval && <Badge variant="outline" className="ml-2 bg-orange-50 text-orange-700 border-orange-200 text-[10px]">needs approval</Badge>}
                      </div>
                    </div>
                    <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ Form Dialog (renders dynamic fields, auto-saves draft) ============
const FormDialog = ({ form, initialPayload, draftId, onClose, onSubmitted }) => {
  const [payload, setPayload] = useState(initialPayload);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const debounceRef = useRef(null);

  const fields = FORM_FIELDS[form.key] || [];
  const requiredFields = fields.filter((f) => f.required);
  const completedRequired = requiredFields.filter((f) => payload[f.name] !== undefined && payload[f.name] !== '' && payload[f.name] !== null).length;
  const progress = requiredFields.length === 0 ? 100 : Math.round((completedRequired / requiredFields.length) * 100);

  const saveDraftDebounced = useCallback((nextPayload) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setSavingDraft(true);
        await axios.post(`${API_URL}/api/data-entry/drafts`, {
          form_key: form.key, payload: nextPayload,
        });
        setLastSaved(new Date());
      } catch (e) { /* noop */ }
      finally { setSavingDraft(false); }
    }, 1500);
  }, [form.key]);

  const updateField = (name, value) => {
    const next = { ...payload, [name]: value };
    setPayload(next);
    saveDraftDebounced(next);
  };

  const submit = async () => {
    for (const f of requiredFields) {
      if (payload[f.name] === undefined || payload[f.name] === '' || payload[f.name] === null) {
        toast.error(`Missing required: ${f.label}`); return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/data-entry/submit`, {
        form_key: form.key, payload,
      });
      // Delete the draft after successful submission
      if (draftId) {
        try { await axios.delete(`${API_URL}/api/data-entry/drafts/${draftId}`); } catch (e) { /* noop */ }
      }
      toast.success(res.data.status === 'pending' ? 'Submitted for approval' : 'Saved successfully');
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> {form.label}
            {form.requires_approval && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">requires approval</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3 -mt-1 mb-1">
          <Progress value={progress} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">{completedRequired}/{requiredFields.length} required</span>
        </div>

        <div className="space-y-3">
          {fields.map((f) => (
            <FieldInput
              key={f.name}
              field={f}
              value={payload[f.name] ?? ''}
              onChange={(v) => updateField(f.name, v)}
            />
          ))}
        </div>

        <DialogFooter className="flex items-center !justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            {savingDraft ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Saving draft…</>
            ) : lastSaved ? (
              <><Save className="h-3 w-3" /> Draft saved {lastSaved.toLocaleTimeString()}</>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="form-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={submitting || progress < 100} data-testid="form-submit-btn">
              {submitting ? 'Submitting…' : (form.requires_approval ? 'Submit for approval' : 'Save record')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FieldInput = ({ field, value, onChange }) => {
  const common = { id: field.name, required: field.required, 'data-testid': `field-${field.name}` };
  if (field.type === 'lineItems') {
    return <LineItemsInput field={field} value={Array.isArray(value) ? value : []} onChange={onChange} />;
  }
  return (
    <div>
      <Label htmlFor={field.name}>{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
      {field.type === 'select' ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger {...common}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => {
              const v = typeof o === 'string' ? o : o.value;
              const l = typeof o === 'string' ? o.replace(/_/g, ' ') : o.label;
              return <SelectItem key={v} value={v}>{l}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      ) : field.type === 'textarea' ? (
        <Textarea {...common} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input {...common} type={field.type} value={value} onChange={(e) => onChange(field.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)} />
      )}
    </div>
  );
};

const LineItemsInput = ({ field, value, onChange }) => {
  const items = value;
  const itemSchema = field.itemSchema || [];

  const addRow = () => {
    const empty = { _rowId: `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` };
    itemSchema.forEach((f) => { empty[f.name] = ''; });
    onChange([...items, empty]);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));
  const updateCell = (idx, name, v) => {
    const next = items.map((it, i) => i === idx ? { ...it, [name]: v } : it);
    onChange(next);
  };
  const total = items.reduce((sum, it) => {
    const qty = parseFloat(it.quantity || 0);
    const cost = parseFloat(it.unit_cost || 0);
    return sum + (qty * cost);
  }, 0);

  return (
    <div className="space-y-2">
      <Label>{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {itemSchema.map((f) => (
                <th key={f.name} className="px-2 py-1.5 text-left text-xs font-medium">{f.label}{f.required && <span className="text-red-500"> *</span>}</th>
              ))}
              <th className="px-2 py-1.5 text-right text-xs font-medium">Line Total</th>
              <th className="px-2 py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={itemSchema.length + 2} className="text-center text-muted-foreground py-3 text-xs">No items yet. Click "Add Item" to begin.</td></tr>
            ) : items.map((it, idx) => {
              const lineTotal = (parseFloat(it.quantity || 0) * parseFloat(it.unit_cost || 0)) || 0;
              return (
                <tr key={it._rowId || `row-${idx}`} className="border-t">
                  {itemSchema.map((f) => (
                    <td key={f.name} className="p-1">
                      <Input
                        type={f.type}
                        value={it[f.name] ?? ''}
                        onChange={(e) => updateCell(idx, f.name, f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`line-${idx}-${f.name}`}
                      />
                    </td>
                  ))}
                  <td className="p-2 text-right tabular-nums text-xs">{lineTotal.toLocaleString()}</td>
                  <td className="p-1 text-center">
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeRow(idx)} data-testid={`remove-line-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {items.length > 0 && (
            <tfoot className="bg-muted/30 font-medium">
              <tr>
                <td colSpan={itemSchema.length} className="px-2 py-1.5 text-right text-xs">Estimated Total</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{total.toLocaleString()}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="add-line-item-btn">
        <span className="mr-1">+</span> Add Item
      </Button>
    </div>
  );
};

// ============ My Drafts ============
const MyDrafts = ({ forms, onContinue }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/data-entry/drafts`);
      setDrafts(res.data || []);
    } catch (e) { toast.error('Failed to load drafts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API_URL}/api/data-entry/drafts/${id}`);
      toast.success('Draft deleted');
      fetchDrafts();
    } catch (e) { toast.error('Delete failed'); }
  };

  if (loading) return <p className="text-muted-foreground text-sm py-8 text-center">Loading…</p>;
  if (drafts.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center">No drafts yet. Forms auto-save as you fill them.</p>;

  return (
    <div className="space-y-2">
      {drafts.map((d) => {
        const form = forms.find((f) => f.key === d.form_key);
        return (
          <Card key={d.id} data-testid={`de-draft-row-${d.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="font-medium">{form?.label || d.form_key}</div>
                <div className="text-xs text-muted-foreground">Updated {new Date(d.updated_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2">
                {form && <Button size="sm" onClick={() => onContinue(d)} data-testid={`continue-draft-${d.id}`}>Continue</Button>}
                <Button size="sm" variant="ghost" onClick={() => deleteDraft(d.id)} data-testid={`delete-draft-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ============ Submissions list ============
const SubmissionsList = ({ mineOnly = false }) => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine_only: mineOnly };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API_URL}/api/data-entry/submissions`, { params });
      setItems(res.data.items || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [mineOnly, statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{items.length} submission{items.length !== 1 && 's'}</CardTitle>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="submissions-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Form</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitter</TableHead>
              <TableHead>Reason / Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              : items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No submissions</TableCell></TableRow>
              : items.map((s) => {
                const Icon = STATUS_ICONS[s.status] || FileText;
                return (
                  <TableRow key={s.id} data-testid={`submission-row-${s.id}`}>
                    <TableCell className="text-xs">{new Date(s.submitted_at).toLocaleString()}</TableCell>
                    <TableCell>{s.form_label}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn(STATUS_COLORS[s.status])}>
                        <Icon className="h-3 w-3 mr-1 inline" />
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{s.submitter_email}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.rejected_reason || (s.target_id ? `→ ${s.target_id.slice(0, 8)}…` : '—')}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ============ Pending Approvals ============
const PendingApprovals = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/data-entry/submissions`, { params: { status: 'pending' } });
      setItems(res.data.items || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const approve = async (id) => {
    try {
      await axios.post(`${API_URL}/api/data-entry/submissions/${id}/approve`);
      toast.success('Approved and recorded');
      fetch(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const doReject = async () => {
    try {
      await axios.post(`${API_URL}/api/data-entry/submissions/${rejecting.id}/reject`, { reason });
      toast.success('Rejected');
      setRejecting(null); setReason('');
      fetch(); onChange?.();
    } catch (e) { toast.error('Reject failed'); }
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading…</p>;
  if (items.length === 0) return <p className="text-center py-8 text-muted-foreground">No pending submissions 🎉</p>;

  return (
    <>
      <div className="space-y-3">
        {items.map((s) => (
          <Card key={s.id} data-testid={`pending-${s.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{s.form_label}</span>
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Pending</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {s.submitter_email} ({s.submitter_role}) · {new Date(s.submitted_at).toLocaleString()}
                  </div>
                  <pre className="bg-muted mt-2 p-2 rounded text-xs overflow-x-auto max-h-40">
                    {JSON.stringify(s.payload, null, 2)}
                  </pre>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setRejecting(s)} data-testid={`reject-${s.id}`}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm" onClick={() => approve(s.id)} data-testid={`approve-${s.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject submission</DialogTitle>
            <DialogDescription>Provide a brief reason — the submitter will see this.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason..." data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button onClick={doReject} data-testid="reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============ Bulk Import ============
const BulkImport = ({ forms }) => {
  const [selectedKey, setSelectedKey] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!selectedKey || !file) { toast.error('Pick a form and file'); return; }
    setSubmitting(true);
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/data-entry/bulk-import/${selectedKey}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`Processed ${res.data.total_rows} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Bulk Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Upload a CSV or .xlsx file. The first row must be field names matching the form. Forms that require approval will create pending submissions.
          </AlertDescription>
        </Alert>
        <div>
          <Label>Form type</Label>
          <Select value={selectedKey} onValueChange={setSelectedKey}>
            <SelectTrigger data-testid="bulk-form-select"><SelectValue placeholder="Choose form…" /></SelectTrigger>
            <SelectContent>
              {forms.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>File (.csv or .xlsx)</Label>
          <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulk-file-input" />
        </div>
        <Button onClick={upload} disabled={submitting || !selectedKey || !file} data-testid="bulk-upload-btn">
          {submitting ? 'Uploading…' : <><Upload className="h-4 w-4 mr-2" /> Import</>}
        </Button>

        {result && (
          <Card className="bg-muted/30 mt-2">
            <CardContent className="p-4 text-sm space-y-1">
              <div><strong>Total rows:</strong> {result.total_rows}</div>
              <div className="text-green-700"><strong>Auto-approved:</strong> {result.approved}</div>
              <div className="text-orange-700"><strong>Pending approval:</strong> {result.pending}</div>
              {result.errors?.length > 0 && (
                <div className="text-red-700">
                  <strong>Errors:</strong> {result.errors.length}
                  <ul className="ml-4 mt-1 list-disc text-xs">
                    {result.errors.slice(0, 5).map((e) => <li key={`err-${e.row}-${(e.error || '').slice(0, 20)}`}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

// ============ Access Matrix (super_admin only) ============
const ROLE_LABELS = {
  super_admin: 'Super Admin', school_admin: 'School Admin', registrar: 'Registrar',
  sponsorship_officer: 'Sponsorship', finance_officer: 'Finance',
  attendance_officer: 'Attendance', hr_officer: 'HR', volunteer_coordinator: 'Volunteer',
};

const AccessMatrix = ({ onSaved }) => {
  const [matrix, setMatrix] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Avoid recomputing the visible roles list on every render (was a .filter
  // in JSX called once per header AND once per row).
  const visibleRoles = useMemo(
    () => roles.filter((r) => r !== 'super_admin'),
    [roles],
  );

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/data-entry/access-matrix`);
      setMatrix(res.data.matrix || []);
      setRoles(res.data.roles || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleRole = async (form_key, role, currentList, currentApproval) => {
    const set = new Set(currentList);
    set.has(role) ? set.delete(role) : set.add(role);
    set.add('super_admin');
    const nextList = Array.from(set);
    try {
      await axios.put(`${API_URL}/api/data-entry/access-matrix/${form_key}`, {
        allowed_roles: nextList, requires_approval: currentApproval,
      });
      // Optimistic local update
      setMatrix(matrix.map((m) => m.form_key === form_key ? { ...m, allowed_roles: nextList } : m));
      onSaved?.();
    } catch (e) { toast.error('Update failed'); }
  };

  const toggleApproval = async (form_key, currentList, value) => {
    try {
      await axios.put(`${API_URL}/api/data-entry/access-matrix/${form_key}`, {
        allowed_roles: currentList, requires_approval: value,
      });
      setMatrix(matrix.map((m) => m.form_key === form_key ? { ...m, requires_approval: value } : m));
    } catch (e) { toast.error('Update failed'); }
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Form Access Matrix</CardTitle>
        <p className="text-sm text-muted-foreground">Check a box to grant a role access to a form. Super Admin always has access.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Form</TableHead>
              {visibleRoles.map((r) => (
                <TableHead key={r} className="text-center text-xs">{ROLE_LABELS[r] || r}</TableHead>
              ))}
              <TableHead className="text-center">Approval</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matrix.map((m) => (
              <TableRow key={m.form_key} data-testid={`matrix-row-${m.form_key}`}>
                <TableCell>
                  <div className="font-medium">{m.label}</div>
                  <div className="text-xs text-muted-foreground">{m.category}</div>
                </TableCell>
                {visibleRoles.map((r) => (
                  <TableCell key={r} className="text-center">
                    <Checkbox
                      checked={m.allowed_roles.includes(r)}
                      onCheckedChange={() => toggleRole(m.form_key, r, m.allowed_roles, m.requires_approval)}
                      data-testid={`matrix-${m.form_key}-${r}`}
                    />
                  </TableCell>
                ))}
                <TableCell className="text-center">
                  <Checkbox
                    checked={m.requires_approval}
                    onCheckedChange={(v) => toggleApproval(m.form_key, m.allowed_roles, !!v)}
                    data-testid={`matrix-approval-${m.form_key}`}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default DataEntryCenterPage;
export { AccessMatrix as DataEntryAccessMatrix };
